import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall, Base_URL } from "../../Common/Path";

// Helper function to normalize custodian name for API compatibility
// Maps display names or legacy codes (e.g. "IBKR", "CHARLES_SCHWAB")
// to the enum values expected by the backend:
//   COINBASE, INTERACTIVE BROKERS, CHARLES SCHWAB
export const normalizeCustodianName = (custodian) => {
  if (!custodian) return custodian;

  const normalized = custodian.replace(/\s+/g, " ").trim().toUpperCase();

  // Coinbase is already in the correct form
  if (normalized === "COINBASE") return "COINBASE";

  // Interactive Brokers - support both old and new labels
  if (
    normalized === "IBKR" ||
    normalized === "INTERACTIVE BROKERS" ||
    normalized === "INTERACTIVE_BROKERS"
  ) {
    return "INTERACTIVE BROKERS";
  }

  // Charles Schwab - support both old and new labels / codes
  if (
    normalized === "CHARLES SCHWAB" ||
    normalized === "CHARLES_SCHWAB" ||
    normalized === "SCHWAB"
  ) {
    return "CHARLES SCHWAB";
  }

  // Fallback: return original value if we don't recognize it
  return custodian;
};

export const Allstrategies = createAsyncThunk(
  "Allstrategies",
  async ({ skip, take, custodian, parent }) => {
    // Normalize custodian name for API compatibility
    const normalizedCustodian = normalizeCustodianName(custodian);
    
    let url = `${Base_URL}/strategies`;
    const params = [];
    // Only add custodian parameter if provided
    if (normalizedCustodian) {
      params.push(`custodian=${encodeURIComponent(normalizedCustodian)}`);
    }
    
    if (skip !== 0) {
      params.push(`skip=${skip}`);
    }
    if (take !== undefined) {
      params.push(`take=${take}`);
    }

    if (parent) {
      params.push(`parentRoleId=${parent}`);
    }

    // Build URL with parameters
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    const result = await ApiCall(url);
    return result;
  }
);

export const addStrategies = createAsyncThunk(
  "addStrategies",
  async ({ body: body, id: id }) => {
    const result = await ApiCall(
      `${Base_URL}/strategies`,
      body,
      "post",
      "multipart"
    );
    return result;
  }
);

export const modifyStrategies = createAsyncThunk(
  "modifyStrategies",
  async ({ id, body }) => {
    const result = await ApiCall(
      `${Base_URL}/strategies/${id}`,

      body,
      "PATCH",
      "multipart"
    );
    return result;
  }
);

export const getCustodians = createAsyncThunk("getCustodians", async () => {
  const result = await ApiCall(`${Base_URL}/custodians`, "", "get");
  return result;
});

export const getCustodianAssets = createAsyncThunk(
  "getCustodianAssets",
  async ({ custodian, query, investmentType, exchange }) => {
    // Normalize custodian name for API compatibility
    const normalizedCustodian = normalizeCustodianName(custodian);
    
    if (exchange != "") {
      const result = await ApiCall(
        `${Base_URL}/assets/custodian/${encodeURIComponent(normalizedCustodian)}?query=${encodeURIComponent(query)}&exchange=${encodeURIComponent(exchange)}&type=${encodeURIComponent(investmentType)}`,
        "",
        "get"
      );
      return result;
    } else {
      const result = await ApiCall(
        `${Base_URL}/assets/custodian/${encodeURIComponent(normalizedCustodian)}?query=${encodeURIComponent(query)}&type=${encodeURIComponent(investmentType)}`,
        "",
        "get"
      );
      return result;
    }
  }
);

export const getAssets = createAsyncThunk("getAssets", async () => {
  const result = await ApiCall(`${Base_URL}/strategy/assets`, "", "get");
  return result;
});

// Accounting time‑series for strategy performance chart
// Supports optional pagination and date / frequency filters:
// GET /accounting/all-portfolio/{strategyId}?skip=&take=&from=&to=&frequency=
export const GetAccounting = createAsyncThunk(
  "GetAccounting",
  async (payload) => {
    // Backwards‑compat: allow passing just the id string/number
    let id;
    let skip = 0;
    let take = 1000;
    let from;
    let to;
    let frequency;

    if (
      typeof payload === "string" ||
      typeof payload === "number"
    ) {
      id = payload;
    } else {
      const params = payload || {};
      id = params.id;
      skip = params.skip ?? 0;
      take = params.take ?? 1000;
      from = params.from;
      to = params.to;
      frequency = params.frequency;
    }

    const queryParts = [];
    if (skip !== undefined && skip !== null) queryParts.push(`skip=${skip}`);
    if (take !== undefined && take !== null) queryParts.push(`take=${take}`);
    if (from) queryParts.push(`from=${encodeURIComponent(from)}`);
    if (to) queryParts.push(`to=${encodeURIComponent(to)}`);
    if (frequency) queryParts.push(`frequency=${encodeURIComponent(frequency)}`);

    let url = `${Base_URL}/accounting/all-portfolio/${encodeURIComponent(
      id
    )}`;
    if (queryParts.length > 0) {
      url += `?${queryParts.join("&")}`;
    }

    const result = await ApiCall(url, "", "get");
    return result;
  }
);

export const GetAccountingPortfolio = createAsyncThunk("GetAccountingPortfolio", async (payload) => {
  // Support both old format {role, id} and new format {skip, take, from, to, frequency, strategyId}
  let strategyId;
  let skip = 0;
  let take = 1000;
  let from;
  let to;
  let frequency;

  if (payload && typeof payload === 'object') {
    // New format with strategyId
    if (payload.strategyId) {
      strategyId = payload.strategyId;
      skip = payload.skip ?? 0;
      take = payload.take ?? 1000;
      from = payload.from;
      to = payload.to;
      frequency = payload.frequency;
    } 
    // Old format with id
    else if (payload.id) {
      strategyId = payload.id;
    }
    // Old format with role and id
    else if (payload.role && payload.id) {
      strategyId = payload.id;
    }
  }

  if (!strategyId) {
    throw new Error('strategyId is required');
  }

  const queryParts = [`strategyId=${encodeURIComponent(strategyId)}`];
  if (skip !== undefined && skip !== null) queryParts.push(`skip=${skip}`);
  if (take !== undefined && take !== null) queryParts.push(`take=${take}`);
  if (from) queryParts.push(`from=${encodeURIComponent(from)}`);
  if (to) queryParts.push(`to=${encodeURIComponent(to)}`);
  if (frequency) queryParts.push(`frequency=${encodeURIComponent(frequency)}`);

  const url = `${Base_URL}/accounting/portfolio?${queryParts.join("&")}`;
  const result = await ApiCall(url, "", "get");
  return result;
});

export const Strategyperformancegraph = createAsyncThunk(
  "Strategyperformancegraph",
  async (id) => {
    try {
      const response = await ApiCall(
        `${Base_URL}/strategy/graph-data?strategyId=${encodeURIComponent(id)}`,
        "",
        "get"
      );
      return response;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
);

export const AlluserStrategies = createAsyncThunk(
  "AlluserStrategies",
  async (id) => {
    const result = await ApiCall(
      `${Base_URL}/strategy/user-strategy/allUserStrategies?strategyId=${encodeURIComponent(id)}`,
      "",
      "get"
    );
    return result;
  }
);

export const GetInvestors = createAsyncThunk("GetInvestors", async ({portal, entityId}) => {
  const result = await ApiCall(
    `${Base_URL}/strategies/all-investors`,
    "",
    "get"
  );
  return result;
});

export const modifyHurdleRate = createAsyncThunk(
  "modifyHurdleRate",
  async (body) => {
    const result = await ApiCall(`${Base_URL}/strategies/users`, body, "PATCH");
    return result;
  }
);

export const GetAddedAccountingFirm = createAsyncThunk("GetAddedAccountingFirm", async () => {
  const result = await ApiCall(`${Base_URL}/accounting/firms`, null, "get");
  return result;
});


export const SupportingAccountingFirm = createAsyncThunk("SupportingAccountingFirm", async () => {
  const result = await ApiCall(`${Base_URL}/accounting/supported-firms`, null, "get");
  return result;
});

export const RequiredFirmKeys = createAsyncThunk("RequiredFirmKeys", async (firm) => {
  const result = await ApiCall(`${Base_URL}/accounting/required-keys/${encodeURIComponent(firm)}`, null, "get");
  return result;
});

export const AddAccountingFirm = createAsyncThunk("AddAccountingFirm", async (data) => {
  const result = await ApiCall(`${Base_URL}/accounting/firm`, data, "post");
  return result;
});

export const GetAccountingFirms = createAsyncThunk("GetAccountingFirms", async () => {
  const result = await ApiCall(`${Base_URL}/accounting/firms`, "", "get");
  return result;
});

export const UpdateAccountingFirm = createAsyncThunk("UpdateAccountingFirm", async (data) => {
  const result = await ApiCall(`${Base_URL}/accounting/firm/${encodeURIComponent(data.id)}`, data, "put");
  return result;
});

export const DeleteAccountingFirm = createAsyncThunk("DeleteAccountingFirm", async (id) => {
  const result = await ApiCall(`${Base_URL}/accounting/firm/${encodeURIComponent(id)}`, "", "delete");
  return result;
});

export const GetFundList = createAsyncThunk("GetFundList", async ({ id, enrich = true }) => {
  const url = `${Base_URL}/accounting/firms/${encodeURIComponent(id)}/funds?enrich=${enrich}`;
  const result = await ApiCall(url, "", "get");
  return result;
});

export const GetPerformanceReturns = createAsyncThunk(
  "GetPerformanceReturns",
  async ({ id, fundId, fromDate, toDate }) => {
    const url = `${Base_URL}/accounting/firms/${encodeURIComponent(id)}/funds/${encodeURIComponent(fundId)}/performance-returns?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
    const result = await ApiCall(url, "", "get");
    return result;
  }
);

export const GetFundInvestors = createAsyncThunk(
  "GetFundInvestors",
  async ({ id, fundId }) => {
    const url = `${Base_URL}/accounting/firms/${encodeURIComponent(id)}/funds/${encodeURIComponent(fundId)}/investors`;
    const result = await ApiCall(url, "", "get");
    return result;
  }
);

export const GetInvestorStatementFile = createAsyncThunk(
  "GetInvestorStatementFile",
  async ({ id, investorId, year, month }) => {
    const url = `${Base_URL}/accounting/firms/${encodeURIComponent(id)}/investors/${encodeURIComponent(investorId)}/investor-statement-file?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`;
    const result = await ApiCall(url, "", "get", "file");
    return result;
  }
);

export const GetFundInvestorStatement = createAsyncThunk(
  "GetFundInvestorStatement",
  async ({ id, fundId, year, month }) => {
    const url = `${Base_URL}/accounting/firms/${encodeURIComponent(id)}/funds/${encodeURIComponent(fundId)}/investor-statement?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`;
    const result = await ApiCall(url, "", "get", "file");
    return result;
  }
);

export const GetInvestorStatement = createAsyncThunk(
  "GetInvestorStatement",
  async ({ id, investorId, year, month }) => {
    const url = `${Base_URL}/accounting/firms/${encodeURIComponent(id)}/investors/${encodeURIComponent(investorId)}/investor-statement?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`;
    const result = await ApiCall(url, "", "get");
    return result;
  }
);

export const GetOnboardedInvestors = createAsyncThunk(
  "GetOnboardedInvestors",
  async (id) => {
    const url = `${Base_URL}/accounting/firms/${encodeURIComponent(id)}/onboarded-investors`;
    const result = await ApiCall(url, "", "get");
    return result;
  }
);

// Schwab file upload API
export const uploadSchwabFiles = createAsyncThunk(
  "uploadSchwabFiles",
  async (files) => {
    try {
      // Create FormData for multipart/form-data upload
      const formData = new FormData();
      
      // Add each file to FormData with 'files' as the parameter name
      files.forEach((file, index) => {
        formData.append('files', file);
      });

      const result = await ApiCall(
        `${Base_URL}/custodians/schwab/import`,
        formData,
        "post",
        "multipart"
      );
      return result;
    } catch (error) {
      throw error;
    }
  }
);

// Get all investors with strategies for a specific custodian
export const getAllInvestorsWithStrategies = createAsyncThunk(
  "getAllInvestorsWithStrategies",
  async ({ custodian }) => {
    try {
      // Normalize custodian name for API compatibility
      const normalizedCustodian = normalizeCustodianName(custodian);
      const url = `${Base_URL}/strategies/all-investors?custodian=${encodeURIComponent(normalizedCustodian)}`;
      const result = await ApiCall(url, "", "get");
      return result;
    } catch (error) {
      throw error;
    }
  }
);

// https://d3c5ndn1v4grfo.cloudfront.net/assets/ibkr?query=appl&type=stocks
